import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import useScrollToTop from "@/hooks/useScrollToTop";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { DASHBOARD_SIGNUP_URL } from "@/config/urls";

const useRevealOnScroll = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setVisible(true);
      },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => {
      if (ref.current) observer.unobserve(ref.current);
    };
  }, []);

  return { ref, visible } as const;
};

const SellYourMusic = () => {
  useScrollToTop();

  useEffect(() => {
    document.title = "Sell Your Music Online | Distribute with YouAux";
  }, []);

  // FAQ schema for Google rich results
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "How much does it cost?",
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "YouAux offers free music distribution with no upfront fees on the Basic plan. You keep 100% ownership of your music. Optional add-ons such as marketing services or expedited delivery are available. Pro and Premium plans add faster delivery, advanced analytics and priority support."
        }
      },
      {
        "@type": "Question",
        name: "Where can I release music?",
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "Distribute your music to 150+ platforms worldwide including Spotify, Apple Music, YouTube Music, Amazon Music, Instagram, TikTok, and more across 200+ countries and territories."
        }
      },
      {
        "@type": "Question",
        name: "Will YouAux help me promote my music?",
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "Yes. YouAux provides artist promotion tools such as playlist pitching, pre-save links, social promotion and analytics to optimize your campaigns. Upgraded plans include additional marketing features and priority support."
        }
      },
      {
        "@type": "Question",
        name: "What is YouAux Pro?",
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "YouAux Pro is designed for active artists and labels, offering faster delivery (priority releases under 24 hours), advanced analytics, YouTube Content ID, discovery tools and priority support."
        }
      },
      {
        "@type": "Question",
        name: "How long does distribution take?",
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "Standard delivery is typically 4–5 days depending on store processing times. Need it faster? Priority releases can be processed in under 24 hours on eligible plans when your metadata and assets meet store guidelines."
        }
      },
      {
        "@type": "Question",
        name: "How are royalties and payouts handled?",
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "YouAux collects royalties from all connected platforms and credits them to your dashboard with transparent reporting. Withdrawals are available once the minimum threshold (\u20ac30) is reached, and collaborator royalty splits can be paid automatically at the source."
        }
      },
      {
        "@type": "Question",
        name: "What is YouAux Premium?",
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "YouAux Premium is our most comprehensive plan, built for growing artists and labels who need scale. It includes everything in Pro plus a higher earnings share (up to 95%), YouTube Official Artist Channel, CMS access for selected users, access to funding under YouAux Select, dedicated account manager, priority release scheduling, custom marketing campaigns, playlist pitching support, ultra‑fast payouts and 24/7 support."
        }
      }
    ]
  } as const;

  return (
    <div className="ophira-bg min-h-screen">
      <Helmet>
        <title>Sell Your Music Online | Distribute with YouAux</title>
        <meta name="description" content="Sell and distribute your music to 150+ platforms like Spotify, Apple Music, YouTube, and more. Keep 100% ownership with YouAux." />
        <link rel="canonical" href="https://youaux.com/sell-your-music" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <Navbar />
      <main className="pt-24">
        {/* Hero */}
        <section className="relative overflow-hidden py-20 md:py-28 bg-gradient-to-b from-ophira-dark via-ophira-dark to-ophira-darkgray/30">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1511379938547-c1f69419868d?q=80&w=2000&auto=format&fit=crop"
              alt="Studio Background"
              className="absolute inset-0 w-full h-full object-cover opacity-20"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/70" />
          </div>
          <div className="container mx-auto px-4 relative z-10 text-center max-w-5xl">
            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight mb-6">
              <span className="text-white">Sell </span>
              <span className="bg-gradient-to-r from-rose-400 via-orange-400 to-amber-300 bg-clip-text text-transparent">Your Music</span>
              <span className="text-white"> Online</span>
            </h1>
            <p className="text-ophira-lightgray text-lg md:text-xl max-w-3xl mx-auto mb-8">
              Make money from your songs by selling and streaming on 150+ platforms like Spotify, Apple Music, YouTube, Amazon Music, TIDAL, and more.
            </p>
            <div className="flex items-center justify-center gap-4">
              <a href={DASHBOARD_SIGNUP_URL}>
                <Button className="px-10 py-6 rounded-full font-semibold text-black bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 shadow-lg hover:shadow-yellow-500/25 transition-all">Sell My Music Online</Button>
              </a>
              <Link to="/pricing">
                <Button variant="outline" className="px-10 py-6 rounded-full border-ophira-yellow/50 text-white hover:bg-ophira-yellow/10">See Plans</Button>
              </Link>
            </div>
          </div>
          {/* partner logos marquee */}
          <div className="container mx-auto px-4 mt-12 opacity-90">
            <LogosMarquee />
          </div>
        </section>

        {/* How it works (images 4 style) */}
        <HowItWorks />

        {/* Upload & Earn section (images 5 style) */}
        <UploadEarn />

        {/* Splits / Collaboration (image 6 style) */}
        <SplitsCollab />

        {/* Why YouAux (images 7-8 style) */}
        <WhyYouAux />

        {/* CTA */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="relative overflow-hidden rounded-3xl p-12 md:p-16 text-center border border-ophira-darkgray/60 bg-gradient-to-r from-[#0f1218] via-[#11151d] to-[#0d1016]">
              <div className="absolute -inset-px pointer-events-none opacity-40" style={{
                background:
                  "radial-gradient(600px 200px at 20% 20%, rgba(234, 179, 8, 0.08), transparent 60%), radial-gradient(600px 200px at 80% 80%, rgba(234, 179, 8, 0.06), transparent 60%)"
              }} />
              <div className="relative z-10 max-w-3xl mx-auto">
                <h2 className="text-4xl md:text-5xl font-extrabold mb-4">Ready to Sell Your Music?</h2>
                <p className="text-ophira-lightgray text-lg md:text-xl mb-8">Join YouAux today, keep 100% ownership of your music, and reach fans worldwide.</p>
                <a href={DASHBOARD_SIGNUP_URL} className="inline-block">
                  <Button className="px-12 py-7 rounded-full text-black bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 shadow-lg hover:shadow-yellow-500/20 transition-all">Sign Up Free</Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* How YouAux Works - Accordion */}
        <section className="py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-4xl md:text-5xl font-extrabold text-center mb-10">How YouAux Works</h2>
            <Accordion type="single" collapsible className="bg-transparent">
              <AccordionItem value="item-1" className="mb-4 border-0">
                <div className="rounded-2xl bg-ophira-darkgray/60 px-6">
                  <AccordionTrigger className="text-left text-white py-5">How much does it cost?</AccordionTrigger>
                  <AccordionContent className="text-ophira-lightgray pb-6">
                    YouAux offers <strong>free music distribution</strong> on our Basic plan, so you can release songs with no upfront fees and keep <strong>100% ownership</strong>. If you need more, our Pro and Premium tiers add <strong>faster delivery</strong>, <strong>advanced analytics</strong>, and <strong>priority support</strong>. Optional add‑ons like marketing services and expedited delivery are available and <strong>clearly priced</strong> on our pricing page.
                  </AccordionContent>
                </div>
              </AccordionItem>
              <AccordionItem value="item-2" className="mb-4 border-0">
                <div className="rounded-2xl bg-ophira-darkgray/60 px-6">
                  <AccordionTrigger className="text-left text-white py-5">Where can I release music?</AccordionTrigger>
                  <AccordionContent className="text-ophira-lightgray pb-6">
                    Distribute to <strong>150+ platforms</strong> including Spotify, Apple Music, YouTube Music, Amazon Music, Deezer, Instagram, and TikTok covering <strong>200+ countries and territories</strong>. Your release is delivered to the major DSPs and social platforms so fans can <strong>stream, download, and create content</strong> with your tracks worldwide.
                  </AccordionContent>
                </div>
              </AccordionItem>
              <AccordionItem value="item-3" className="mb-4 border-0">
                <div className="rounded-2xl bg-ophira-darkgray/60 px-6">
                  <AccordionTrigger className="text-left text-white py-5">Will YouAux help me promote my music?</AccordionTrigger>
                  <AccordionContent className="text-ophira-lightgray pb-6">
                    Yes. YouAux provides <strong>artist promotion tools</strong> like playlist pitching, <strong>pre‑save links</strong>, social media promotion, and <strong>analytics</strong> to track performance. Our team can support release strategy and audience growth, while Pro/Premium plans unlock <strong>advanced marketing features</strong> and priority support to help your songs reach new listeners.
                  </AccordionContent>
                </div>
              </AccordionItem>
              <AccordionItem value="item-4" className="mb-4 border-0">
                <div className="rounded-2xl bg-ophira-darkgray/60 px-6">
                  <AccordionTrigger className="text-left text-white py-5">How long does distribution take?</AccordionTrigger>
                  <AccordionContent className="text-ophira-lightgray pb-6">
                    Standard delivery is typically <strong>4–5 days</strong>, though individual store review times may vary. Need it faster? Choose <strong>priority release</strong> on eligible plans to have your music processed in <strong>under 24 hours</strong>, provided your audio, artwork and metadata meet platform guidelines.
                  </AccordionContent>
                </div>
              </AccordionItem>
              <AccordionItem value="item-5" className="mb-4 border-0">
                <div className="rounded-2xl bg-ophira-darkgray/60 px-6">
                  <AccordionTrigger className="text-left text-white py-5">How are royalties and payouts handled?</AccordionTrigger>
                  <AccordionContent className="text-ophira-lightgray pb-6">
                    YouAux collects royalties from every connected store and credits them to your <strong>dashboard with transparent reports</strong>. Set your payout details to withdraw once you reach the <strong>€30 minimum threshold</strong>. If you collaborate, our <strong>royalty splits at source</strong> feature can automatically distribute earnings to each contributor.
                  </AccordionContent>
                </div>
              </AccordionItem>
              <AccordionItem value="item-6" className="mb-4 border-0">
                <div className="rounded-2xl bg-ophira-darkgray/60 px-6">
                  <AccordionTrigger className="text-left text-white py-5">What is YouAux Pro?</AccordionTrigger>
                  <AccordionContent className="text-ophira-lightgray pb-6">
                    <strong>YouAux Pro</strong> is built for active artists and labels. It includes <strong>priority distribution</strong> (eligible releases can go live in under <strong>24 hours</strong>), <strong>advanced analytics and reporting</strong>, YouTube <strong>Content ID</strong>, discovery tools, and <strong>priority support</strong> everything you need to scale your catalog with speed and insight.
                  </AccordionContent>
                </div>
              </AccordionItem>
              <AccordionItem value="item-7" className="mb-8 border-0">
                <div className="rounded-2xl bg-ophira-darkgray/60 px-6">
                  <AccordionTrigger className="text-left text-white py-5">What is YouAux Premium?</AccordionTrigger>
                  <AccordionContent className="text-ophira-lightgray pb-6">
                    <strong>YouAux Premium</strong> is our most comprehensive plan for growing artists and labels. It includes everything in Pro plus a higher <strong>earnings share (up to 95%)</strong>, <strong>Official Artist Channel</strong> on YouTube, <strong>CMS access</strong> for selected users, <strong>access to funding</strong> (YouAux Select), <strong>dedicated account manager</strong>, <strong>priority release scheduling</strong>, <strong>custom marketing campaigns</strong> with <strong>playlist pitching support</strong>, <strong>ultra‑fast payouts</strong>, and <strong>24/7 support</strong>.
                  </AccordionContent>
                </div>
              </AccordionItem>
            </Accordion>
            <div className="mt-12  flex justify-center">
              <a href={DASHBOARD_SIGNUP_URL} className="inline-flex">
                <Button className="ophira-outline-button px-10 py-6 rounded-full">Release Your Music</Button>
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

const platformLogos = [
  "/assets/logos/jiosaavn.svg",
  "/assets/logos/amazonmusic.svg",
  "/assets/logos/tidal.svg",
  "/assets/logos/anghami.svg",
  "/assets/logos/deezer.svg",
  "/assets/logos/spotify.svg",
  "/assets/logos/youtube.svg",
  "/assets/logos/applemusic.svg",
  "/assets/logos/instagram.svg",
  "/assets/logos/tiktok.svg",
  "/assets/logos/soundcloud.svg",
  "/assets/logos/snapchat.svg",
];

const LogosMarquee = () => {
  return (
    <div className="relative overflow-hidden">
      <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-ophira-dark to-transparent" />
      <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-ophira-dark to-transparent" />
      <div className="flex gap-10 whitespace-nowrap animate-[logo-marquee_25s_linear_infinite]">
        {[...platformLogos, ...platformLogos].map((src, i) => (
          <img key={i} src={src} alt="partner" className="h-7 md:h-9 opacity-80 hover:opacity-100 transition-opacity" />
        ))}
      </div>
    </div>
  );
};

const HowItWorks = () => {
  const { ref, visible } = useRevealOnScroll();
  return (
    <section ref={ref} className={`py-24 bg-gradient-to-b from-ophira-dark to-ophira-darkgray/20 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
      <div className="container mx-auto px-4">
        <h2 className="text-4xl md:text-5xl font-extrabold text-center mb-4">How to Sell Your Music Online</h2>
        <p className="text-ophira-lightgray text-center max-w-3xl mx-auto mb-12">It’s never been easier. Follow these steps and start earning from your music.</p>
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {[
            { t: "Upload Your Music", d: "Easily upload your tracks, artwork, and release details in minutes." },
            { t: "We Deliver to Stores", d: "We send your release to 150+ streaming services worldwide." },
            { t: "You Get Paid", d: "Earn from streams and downloads. Keep 100% ownership of your music." },
          ].map((s, i) => (
            <div key={i} className="group relative rounded-xl bg-ophira-darkgray/40 border border-ophira-darkgray/60 hover:bg-ophira-darkgray/60 hover:border-ophira-yellow/30 transition-colors p-8 md:p-10">
              <div className="mb-4 inline-flex items-center justify-center w-10 h-10 rounded-full border border-ophira-yellow/60 text-ophira-yellow font-bold">{i + 1}</div>
              <h3 className="text-xl md:text-2xl font-semibold mb-2">{s.t}</h3>
              <p className="text-ophira-lightgray leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 flex justify-center">
          <a href={DASHBOARD_SIGNUP_URL} className="inline-flex">
            <Button className="ophira-outline-button px-10 py-6 rounded-full hover:bg-ophira-yellow/90">Release Your Music</Button>
          </a>
        </div>
      </div>
    </section>
  );
};

const UploadEarn = () => {
  const { ref, visible } = useRevealOnScroll();
  return (
    <section ref={ref} className={`py-24 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
      <div className="container mx-auto px-4 grid lg:grid-cols-2 gap-12 items-center">
        <div className="relative">
          <div className="rounded-2xl border border-ophira-darkgray/50 p-5 bg-ophira-dark/50 overflow-hidden">
            <img 
              src="https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?q=80&w=1200&auto=format&fit=crop" 
              alt="Music Production Studio" 
              className="w-full h-40 md:h-52 rounded-xl object-cover hover:scale-105 transition-transform duration-500"
            />
          </div>
        </div>
        <div>
          <h2 className="text-4xl md:text-5xl font-extrabold mb-4"><span className="bg-gradient-to-r from-rose-400 to-violet-400 bg-clip-text text-transparent">Upload Music</span> & Start Earning</h2>
          <p className="text-ophira-lightgray text-lg mb-6">Create an account, upload your music, choose your stores, and that’s it. We handle delivery and payouts, so you can focus on the music.</p>
          <a href={DASHBOARD_SIGNUP_URL}>
            <Button className="ophira-button px-10 py-6 rounded-full">Sign Up Now</Button>
          </a>
        </div>
      </div>
    </section>
  );
};

const SplitsCollab = () => {
  const { ref, visible } = useRevealOnScroll();
  return (
    <section ref={ref} className={`py-24 bg-gradient-to-br from-[#20131f] via-[#191a25] to-[#10131b] transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
      <div className="container mx-auto px-4 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <h2 className="text-4xl md:text-5xl font-extrabold mb-4">Splits: The Power of <span className="bg-gradient-to-r from-rose-400 via-fuchsia-400 to-indigo-400 bg-clip-text text-transparent">Collaboration</span></h2>
          <p className="text-ophira-lightgray text-lg mb-6">Divide royalties from any collaboration seamlessly. Add collaborators and assign percentages payments are automatically split. Free with every plan.</p>
          <Link to="/pricing">
            <Button className="ophira-outline-button px-10 py-6 rounded-full">Learn More</Button>
          </Link>
        </div>
        <div className="rounded-2xl overflow-hidden border border-ophira-darkgray/50">
          <img src="https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=1200&auto=format&fit=crop" alt="Collaboration" className="w-full h-64 md:h-80 lg:h-96 object-cover hover:scale-105 transition-transform duration-500" />
        </div>
      </div>
    </section>
  );
};

const WhyYouAux = () => {
  const { ref, visible } = useRevealOnScroll();
  return (
    <section ref={ref} className={`pt-16 pb-8 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
      <div className="container mx-auto px-4">
        <h2 className="text-4xl md:text-5xl font-extrabold mb-12">Sell Your Music with YouAux</h2>
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div className="rounded-2xl border border-ophira-darkgray/50 p-5 bg-ophira-dark/50 overflow-hidden">
            <img 
              src="https://images.unsplash.com/photo-1571330735066-03aaa9429d89?q=80&w=1200&auto=format&fit=crop" 
              alt="Music Distribution Platform" 
              className="aspect-[4/5] rounded-xl object-cover hover:scale-105 transition-transform duration-500"
            />
          </div>
          <ul className="space-y-6">
            {[
              { h: "Global Distribution", p: "Send your music to 200+ countries and territories across 150+ platforms." },
              { h: "Keep 100% Ownership", p: "Creative control and full royalty retention. Your music, your rights." },
              { h: "YouTube and Meta Content ID", p: "Protect and monetize your music across YouTube and Meta with advanced Content ID technology." },
              { h: "Unlimited Releases", p: "YouAux offers unlimited releases with unlimited artist without upfront fees. You keep 100% ownership of your music." },
              { h: "Fast Release Time", p: "Get your music live in 72 hours & priority releases available under 24 hours." },
              { h: "Playlist Pitching", p: "Get your tracks pitched to curated playlists and increase your discoverability." },
              { h: "Ultimate Support", p: "Get 24/7 support from our team to help you with any issues you may have." },
            ].map((item, i) => (
              <li key={i} className="bg-ophira-dark/40 rounded-xl p-6 border border-ophira-yellow/10 hover:border-ophira-yellow/30 transition-colors">
                <h3 className="text-xl font-semibold mb-2">{item.h}</h3>
                <p className="text-ophira-lightgray">{item.p}</p>
              </li>
            ))}
            {/* <li>
              <a href={DASHBOARD_SIGNUP_URL}>
                <Button className="ophira-button px-8 py-6 rounded-full">Sign Up Now</Button>
              </a>
            </li> */}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default SellYourMusic;



